// Pixie (internal name "pixo") — catalog + asset helpers. Keep in sync with backend/services/pixo.py (ITEMS).
// Display names come from i18n (t.pixo.items[key]); English is the source language.

export const PIXO_ITEMS = [
  { key: "cap", rarity: "common", slot: "head", edition: 1 },
  { key: "bow", rarity: "common", slot: "head", edition: 1 },
  { key: "heart_glasses", rarity: "common", slot: "eyes", edition: 1 },
  { key: "bandana", rarity: "common", slot: "head", edition: 1 },
  { key: "card_backpack", rarity: "uncommon", slot: "back", edition: 1 },
  { key: "star_plush", rarity: "uncommon", slot: "hand", edition: 1 },
  { key: "sunglasses", rarity: "uncommon", slot: "eyes", edition: 1 },
  { key: "bunny_ears", rarity: "uncommon", slot: "head", edition: 1 },
  { key: "champion_crown", rarity: "rare", slot: "head", edition: 1 },
  { key: "magic_wand", rarity: "rare", slot: "hand", edition: 1 },
  { key: "sleeve_cape", rarity: "rare", slot: "back", edition: 1 },
  { key: "pixel_wings", rarity: "legendary", slot: "back", edition: 1 },
  { key: "holo_aura", rarity: "legendary", slot: "effect", edition: 1 },
  // exclusive: only from the Grail Fish (fishing), never from packs; not counted in the pack collection
  { key: "fisherman_hat", rarity: "legendary", slot: "head", edition: 1, exclusive: "fishing" },
  { key: "beanie", rarity: "common", slot: "head", edition: 1 },
  { key: "cat_ears", rarity: "uncommon", slot: "head", edition: 1 },
  { key: "beanie_badge", rarity: "uncommon", slot: "head", edition: 1 },
  { key: "top_hat", rarity: "rare", slot: "head", edition: 1 },
  { key: "lynx_ears", rarity: "legendary", slot: "head", edition: 1 },
  // batch 17 — exclusive: 1% from a Buried Chest (Treasure Map); drawn over the gem
  { key: "pirate_hat", rarity: "legendary", slot: "head", edition: 1, exclusive: "treasure" },
  // batch 22 — exclusive: the first 151 members (Founder medal rule)
  { key: "founder_pin", rarity: "legendary", slot: "pin", edition: 1, exclusive: "founder" },
  // batch 22 — exclusive: a tiny chance when harvesting in the garden; sits over the gem
  { key: "gardener_hat", rarity: "legendary", slot: "head", edition: 1, exclusive: "garden" },
  // patch 29 — exclusive: 30 days in a row of Apoio ⚡
  { key: "lightning_scepter", rarity: "legendary", slot: "hand", edition: 1, exclusive: "support" },
];

// 1st Edition binder order (numbered like a TCG set list, by rarity). Exclusives live in their own tab.
export const SET1 = ["cap", "bow", "heart_glasses", "bandana", "beanie",
  "card_backpack", "star_plush", "sunglasses", "bunny_ears", "cat_ears", "beanie_badge",
  "champion_crown", "magic_wand", "sleeve_cape", "top_hat",
  "pixel_wings", "holo_aura", "lynx_ears"];
export const EXCLUSIVES = ["fisherman_hat", "pirate_hat", "founder_pin", "gardener_hat", "lightning_scepter"];
// back-slot items drawn in FRONT of the body (the Deck Bag hangs in front) and head items drawn over the gem
export const FRONT_ITEMS = new Set(["card_backpack"]);
export const OVER_GEM_ITEMS = new Set(["pirate_hat", "gardener_hat"]);
// Weather Jars + Fortune Cookies come in phase 2: until then the UI shows no weather / fortune effects.
export const PHASE2 = false;
export const BREAK_VALUE = { common: 5, uncommon: 15, rare: 50, legendary: 200 };

// Item editions: every item belongs to a release. New releases get a new number + i18n label (t.pixo.edition[n]).

export const ITEM_BY_KEY = Object.fromEntries(PIXO_ITEMS.map((i) => [i.key, i]));

// Food from the daily chest: eating gives XP; the legendary one also makes the Pixie glow for 7 days.
export const FOOD = [
  { key: "cookie", rarity: "common", xp: 5 },
  { key: "onigiri", rarity: "common", xp: 5 },
  { key: "bubble_tea", rarity: "uncommon", xp: 12 },
  { key: "strawberry_cake", rarity: "rare", xp: 30 },
  { key: "golden_nata", rarity: "legendary", xp: 2026 },
  { key: "fortune_cookie", rarity: "uncommon", xp: 5, shopOnly: true },
];
export const FOOD_BY_KEY = Object.fromEntries(FOOD.map((f) => [f.key, f]));

// Fishing: 14 fictional fish (sprite size in px). Keep in sync with backend/services/fishing.py (FISH).
export const FISH = [
  { key: "sleevefish", rarity: "common", w: 24, h: 14 },
  { key: "pebble_perch", rarity: "common", w: 21, h: 14 },
  { key: "shuffle_shad", rarity: "common", w: 24, h: 14 },
  { key: "bulk_carp", rarity: "common", w: 24, h: 15 },
  { key: "penny_minnow", rarity: "common", w: 19, h: 12 },
  { key: "dice_dace", rarity: "common", w: 22, h: 14 },
  { key: "foilfin", rarity: "uncommon", w: 24, h: 14 },
  { key: "mintscale", rarity: "uncommon", w: 26, h: 14 },
  { key: "deckbox_pike", rarity: "uncommon", w: 27, h: 12 },
  { key: "playmat_ray", rarity: "uncommon", w: 26, h: 14 },
  { key: "holo_koi", rarity: "rare", w: 26, h: 14 },
  { key: "prism_eel", rarity: "rare", w: 35, h: 10 },
  { key: "booster_bass", rarity: "rare", w: 25, h: 15 },
  { key: "grail_fish", rarity: "legendary", w: 33, h: 18 },
];
export const FISH_BY_KEY = Object.fromEntries(FISH.map((f) => [f.key, f]));

export const RARITY = {
  common: { color: "#9C96A8", order: 0 },
  uncommon: { color: "#4BE38A", order: 1 },
  rare: { color: "#6FA8FF", order: 2 },
  legendary: { color: "#F5C518", order: 3 },
};

const BASE = "/pixo";
export const pixoAsset = {
  body: (pose) => `${BASE}/body-${pose}.png`,
  gem: (tier) => `${BASE}/gems/${tier || "quartzo"}.png`,
  item: (key) => `${BASE}/items/${key}.png${key === "bow" ? "?v=22" : ""}`, // v: bust the cache when a sprite is redrawn
  pack: `${BASE}/pack.png`,
  packGod: `${BASE}/pack-god.png`,
  food: (key) => `${BASE}/food/${key}.png`,
  chest: `${BASE}/chest.png`,
  chestOpen: `${BASE}/chest-open.gif`,
  fireworks: `${BASE}/fireworks.png`,
  fish: (key) => `${BASE}/fishing/fish/${key}.png`,
  fishing: (name) => `${BASE}/fishing/${name}.png`,
  opening: (rarity, kind) => (kind === "god" ? `${BASE}/booster-open-god.gif` : `${BASE}/booster-open-${rarity}.gif`),   // patch 34: God Packs open as a God Pack
  z: `${BASE}/z.png`,
  sparkle: `${BASE}/sparkle.png`,
  shop: (key) => `${BASE}/shop/${key}.png`,
  dig: (name) => `${BASE}/dig/${name}.png`,
  buriedOpen: `${BASE}/dig/buried-open.gif`,
  mapPiece: (n) => `${BASE}/map/piece${n}.png`,
  ach: (key) => `${BASE}/achievements/${key}.png`,
};

/** Shop icon for an item key (weather jars share one jar, tinted by CSS filter). */
export const JAR_FILTER = { rain: "none", fog: "grayscale(1) brightness(1.2)", sun: "hue-rotate(180deg) saturate(1.6)", storm: "brightness(.7) hue-rotate(40deg)" };
export function shopIcon(key) {
  if (key.startsWith("jar_")) return { src: pixoAsset.shop("jar"), filter: JAR_FILTER[key.slice(4)] };
  if (key === "bait3") return { src: pixoAsset.fish("foilfin") };
  if (["cookie", "bubble_tea", "strawberry_cake"].includes(key)) return { src: pixoAsset.food(key) };
  if (key === "anglers_kit") return { src: pixoAsset.shop("lucky_lure") };
  if (key === "two_maps" || key === "lucky_week") return { src: pixoAsset.shop("treasure_map") };
  if (key === "snack_box") return { src: pixoAsset.food("strawberry_cake") };
  // batch 22 · garden
  const GD = { garden_stones: "stones", garden_lantern: "lantern", garden_bench: "bench_0", garden_bed: "bed_0" };
  if (GD[key]) return { src: `/pixo/garden/${GD[key]}.png` };
  return { src: pixoAsset.shop(key) };
}

export const OPENING_MS = 1440; // length of booster-open-*.gif
export const CHEST_MS = 1680; // length of chest-open.gif

/** Preload images so animations never flash. */
export function preloadPixo(extra = []) {
  const urls = [
    ...["happy", "joy", "yawn", "sleep", "lying"].map(pixoAsset.body),
    ...PIXO_ITEMS.map((i) => pixoAsset.item(i.key)),
    pixoAsset.pack,
    pixoAsset.chest,
    ...FOOD.map((f) => pixoAsset.food(f.key)),
    pixoAsset.z,
    pixoAsset.sparkle,
    ...extra,
  ];
  urls.forEach((u) => {
    const img = new Image();
    img.src = u;
  });
}

const GEM_FILES = {
  quartzo: "gem-1-quartzo", ametista: "gem-2-ametista", esmeralda: "gem-3-esmeralda", safira: "gem-4-safira",
  topazio: "gem-5-topazio", rubi: "gem-6-rubi", diamante: "gem-7-diamante", ouro: "gem-8-ouro", prisma: "gem-9-prisma",
};
/** Full-size (96px) gem image for a tier key. */
export const gemImage = (tier) => `/gems/${GEM_FILES[tier] || GEM_FILES.quartzo}-96.png`;

/** Patch 34: item numbers look like #001 (more digits once an item passes 999). */
export const serialText = (n) => `#${String(n).padStart(3, "0")}`;
